import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { isMobile } from 'react-device-detect';
import DatePicker, { DateObject } from 'react-multi-date-picker';
import InputIcon from 'react-multi-date-picker/components/input_icon';
import styles from './styles.module.css';
import { useTranslation } from 'react-i18next';
import { WisorySelect } from 'components/Inputs/components/WisorySelect';
import clsx from 'clsx';
import 'react-multi-date-picker/styles/layouts/mobile.css';
import sv from './locales/sv';
import en from './locales/en';
import { TIMESPAN, TIMESPAN_HOURS } from 'pages/profile/constants';
import { getHours } from 'date-fns';

type Props = {
  selectedDate?: Date;
  className?: string;
  minDate?: Date;
  period: TIMESPAN;
  onChange: (timeslot?: TimeSlot) => void;
};

export type TimeSlot = {
  startDate?: Date;
  endDate?: Date;
};

export const getTimeSpan = (startDate: Date, endDate: Date) => {
  const timespan =
    Object.keys(TIMESPAN_HOURS).find((key) => {
      const range = TIMESPAN_HOURS[key as keyof typeof TIMESPAN_HOURS];

      return getHours(startDate) >= range.startHours && getHours(endDate) <= range.endHours;
    }) || TIMESPAN.ALL_DAY;

  return timespan;
};

export const getTimePeriodsFromDate = (date: Date, timespan: TIMESPAN) => {
  const selectedDate = dayjs(date);
  const selectedTimespan = TIMESPAN_HOURS[timespan];

  let startDate = selectedDate.set('hour', selectedTimespan.startHours).set('minute', 0).set('second', 0);
  let endDate = selectedDate.set('hour', selectedTimespan.endHours).set('minute', 0).set('second', 0);

  return {
    startDate: startDate.toDate(),
    endDate: endDate.toDate(),
  };
};

const WEEKENDS = {
  gregorian: [0, 6],
};

export default function TimeSlotPicker(props: Props) {
  const { selectedDate, minDate, period: defaultPeriod, className = '', onChange } = props;
  const [value, setValue] = useState(selectedDate);
  const [period, setPeriod] = useState<TIMESPAN>(defaultPeriod);
  const { t } = useTranslation();
  const timespanOptions = useMemo(() => {
    return Object.keys(TIMESPAN_HOURS).map((key) => {
      const timespan = TIMESPAN_HOURS[key as keyof typeof TIMESPAN_HOURS];
      const now = dayjs();
      const startTime = now.set('hours', timespan.startHours).set('minutes', 0);
      const endTime = now.set('hours', timespan.endHours).set('minutes', 0);

      const rangeText = `${startTime.format('HH:mm')} - ${endTime.format('HH:mm')}`;

      return {
        value: TIMESPAN[key as TIMESPAN],
        label: t(`customer_info_form_${key.toLowerCase()}`) + ` (${rangeText})`,
      };
    });
  }, [t]);

  const setTimePeriod = (newPeriod: TIMESPAN, newDate?: Date) => {
    const timePeriod = newDate
      ? getTimePeriodsFromDate(newDate, newPeriod)
      : { startDate: undefined, endDate: undefined };

    setPeriod(newPeriod);
    setValue(newDate);

    onChange(timePeriod);
  };

  let mobileProps = isMobile
    ? {
        mobileLabels: {
          OK: t('REACT_BOOKING_FRONTEND.common.ok'),
          CANCEL: t('REACT_BOOKING_FRONTEND.common.cancel'),
        },
      }
    : {};

  return (
    <div className={`${className} flex items-center font-openSans`}>
      <div className={`w-1/2 text-black mr-8 ${styles.dateInputBlock} sm:w-1/3`}>
        <DatePicker
          locale={WISORY_SDK_SETTINGS.LOCALE === 'sv' ? sv : en}
          placeholder="Select Date"
          value={value}
          minDate={minDate}
          calendarPosition="top-center"
          weekStartDayIndex={1}
          highlightToday={true}
          render={
            <InputIcon
              className={`text-black ${styles.dateInput}`}
              placeholder={t('REACT_BOOKING_FRONTEND.suggestedSlots.timeslotPlaceholder')}
            />
          }
          className={clsx(styles.datePicker, {
            'rmdp-mobile': isMobile,
          })}
          containerClassName={styles.datePickerContainer}
          mapDays={({ date }) => {
            if (WEEKENDS.gregorian.includes(date.weekDay.index)) {
              return {
                disabled: true,
              };
            }
          }}
          onChange={(selectedDates) => {
            if (!selectedDates) {
              onChange({ startDate: undefined, endDate: undefined });
            }

            if (selectedDates instanceof DateObject) {
              const dateInDatePicker = selectedDates.toDate();
              setTimePeriod(period, dateInDatePicker);
            }
          }}
          format="YYYY-MM-DD"
          {...mobileProps}
        />
      </div>
      <div className="w-1/2 sm:w-2/3">
        <WisorySelect
          title="Select Timeslot"
          name="period-dropdown"
          value={period}
          onChange={(e) => {
            setTimePeriod(e.target.value as TIMESPAN, value);
          }}
          className={styles.dropdown}
        >
          {timespanOptions.map((item) => (
            <option key={item.value} value={item.value} className="text-black">
              {item.label}
            </option>
          ))}
        </WisorySelect>
      </div>
    </div>
  );
}
